import { erpApi } from "@/shared/axios";
import { ROUTER_PATHS, urlBackend } from "@/shared/constants";
import { AlertSeverityEnum, IDataServicioDigitalParams, UseMutationsParams } from "@/shared/interfaces";
import { IServicioDigital, IServicioDigitalResp } from "@/shared/interfaces/erp/soluciones_digitales/servicios_digitales.interface";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { useUiStore } from "@/store/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const { post, get, put } = erpApi();

///* Obtener todos los servicios digitales
export const useGetServicioDigital = (params: IDataServicioDigitalParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['serviciodigital', ...Object.values(params || {})],
    queryFn: async () => {
      return await getServicioDigital(params);
    },
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getServicioDigital = async (params?: IDataServicioDigitalParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<IServicioDigitalResp>(
    `${urlBackend}api/v1/serviciodigital/?${queryParams}`,
    true
  );
  return resp;
};

///* Obtener servicio digital por uid
export const useGetServicioDigitalById = (id: string) => {
  return useQuery({
    queryKey: ['serviciodigital', id],
    queryFn: async () => {
      return getServicioDigitalById(id);
    },
    refetchOnWindowFocus: false,
    gcTime: 0
  });
};

export const getServicioDigitalById = async (id: string) => {
    try {
      return await get<IServicioDigital>(`${urlBackend}api/v1/serviciodigital/${id}`, true);
    } catch (error) {
      handleAxiosError(error);
    }
};

///* Guardar un servicio digital
export const usePostServicioDigital = ({
  navigate,
  enableSuccessAlert=true,
  returnUrl
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: postServicioDigital,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['serviciodigital'] });
      setAlert(enableSuccessAlert, data.message, data.status);
      navigate && navigate(returnUrl ?? `${ROUTER_PATHS.solucionesDigitales.servicios_nav}`);
    },
    onError: (e) => {
      setIsGlobalLoading(false);
      handleAxiosError(e);
    },
  });
};

export const postServicioDigital = async (data: Partial<IServicioDigital>) => {
  const res = await post<IServicioDigital>(`${urlBackend}api/v1/serviciodigital/`, data, true);
  return res;
};

///* Actualizar servicio digital
export const usePutServicioDigital = ({
  navigate,
  enableSuccessAlert=true,
  returnUrl,
}: UseMutationsParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      data: IDataServicioDigitalParams;
    }) => {
      return putServicioDigital(params);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['serviciodigital'] });
      setAlert(enableSuccessAlert, data.message, AlertSeverityEnum.success);
      navigate && navigate(returnUrl ?? `${ROUTER_PATHS.solucionesDigitales.servicios_nav}`);
    },
    onError: (error) => {
      handleAxiosError(error);
    }
  });
};

export const putServicioDigital = async (
  { id, data }: {
    id: string,
    data: IDataServicioDigitalParams
  }
) => {
  const res = await put<IServicioDigital>(`${urlBackend}api/v1/serviciodigital/${id}/`, data, true);
  return res;
};